import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import AlbumFolderSubscriptionOption from '../AlbumFolderSubscriptionOption';
import {
  getAlbumFolderSubscriptionCartItem,
  getDigitalBundleCartItem,
  getDigitalDownloadCartItem,
} from '../../../../../services/checkout/redux/helpers';
import { hasPurchasedAlbumFolderIdentity } from '../../helpers';
import DigitalDownloadOption from '../DigitalDownloadOption';
import CART_ITEM from '../../../../../services/checkout/constants/cartItems';
import DigitalBundleOption from '../DigitalBundleOption';

const propTypes = {
  activeIdentity: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  selectedIdentity: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    prepaid_photo_print_packages_enabled: PropTypes.bool,
  }).isRequired,
  photoPricing: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  digitalBundles: PropTypes.array,
  selectedIdentities: PropTypes.array,
  onAlbumFolderSubscriptionToggle: PropTypes.func.isRequired,
  onDigitalBundleToggle: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
};

const DownloadsSelector = ({
  activeIdentity,
  selectedIdentity,
  albumFolder,
  photoPricing,
  digitalBundles,
  selectedIdentities,
  onAlbumFolderSubscriptionToggle,
  onDigitalBundleToggle,
  cart,
  onCartItemUpdate,
  onDigitalDownloadToggle,
}) => {
  const hasPurchasedAlbumFolderSubscription = hasPurchasedAlbumFolderIdentity(
    albumFolder,
    activeIdentity,
    selectedIdentities,
  );

  const albumPrintPackageCartItems = cart.filter(item =>
    [CART_ITEM.ALBUM_PRINT_PACKAGE, CART_ITEM.PURCHASED_PRINT_PACKAGE].includes(
      item.type,
    ),
  );
  const hasPrintPackageCartItems = albumPrintPackageCartItems.length > 0;
  const canPurchasePhotoPrintItems =
    !photoPricing?.requires_package_purchase || hasPrintPackageCartItems;

  if (
    !(activeIdentity && albumFolder?.price?.value > 0) &&
    !(
      albumFolder?.prepaid_photo_pricings_enabled &&
      photoPricing?.digital_downloads_price?.value > 0
    ) &&
    !(
      albumFolder?.prepaid_digital_bundles_enabled &&
      (digitalBundles ?? []).length > 0
    )
  ) {
    return null;
  }

  return (
    <Fragment>
      <h4 className="mt-2">Digital Only</h4>

      {activeIdentity && albumFolder?.price?.value > 0 && (
        <AlbumFolderSubscriptionOption
          albumFolder={albumFolder}
          cartItem={getAlbumFolderSubscriptionCartItem(cart, {
            albumFolderId: albumFolder.id,
            identityId: activeIdentity.id,
          })}
          onSubscriptionToggle={() =>
            onAlbumFolderSubscriptionToggle({
              albumFolder,
              identity: activeIdentity,
            })
          }
          isPurchased={hasPurchasedAlbumFolderSubscription}
        />
      )}
      {albumFolder?.prepaid_digital_bundles_enabled &&
        digitalBundles?.length > 0 && (
          <Fragment>
            {digitalBundles.map(bundle => (
              <DigitalBundleOption
                key={bundle.id}
                digitalBundle={bundle}
                disabled={
                  bundle.requires_package_purchase && !hasPrintPackageCartItems
                }
                cartItem={getDigitalBundleCartItem(cart, {
                  identityId: activeIdentity?.id,
                  digitalBundleId: bundle?.id,
                  prePaid: true,
                })}
                onDigitalBundleToggle={() =>
                  onDigitalBundleToggle({
                    identity: activeIdentity,
                    digitalBundle: bundle,
                    prePaid: true,
                  })
                }
              />
            ))}
          </Fragment>
        )}
      {albumFolder?.prepaid_photo_pricings_enabled &&
        photoPricing?.digital_downloads_price?.value > 0 && (
          <DigitalDownloadOption
            photoPricing={photoPricing}
            disabled={!canPurchasePhotoPrintItems}
            onUpdate={onCartItemUpdate}
            cartItem={getDigitalDownloadCartItem(cart, {
              identityId: activeIdentity?.id,
              prePaid: true,
            })}
            onDigitalDownloadToggle={() =>
              onDigitalDownloadToggle({
                identity: activeIdentity,
                albumFolder,
                album: selectedIdentity?.album_membership?.album,
                photoPricing,
                prePaid: true,
              })
            }
          />
        )}
    </Fragment>
  );
};

DownloadsSelector.propTypes = propTypes;

export default DownloadsSelector;
